// @flow
/* eslint-env browser */

import { createSelector } from 'reselect';

import {
  selectIsFailureOneOrder,
  selectIsFetchingOneOrder,
  selectIsLoadedOneOrder,
  selectIsToRefetchOneOrder,
  selectOrder,
  fetchOrder,
  type OrderId,
  type OrderIdProp,
  type Currency,
} from '@gandi/billing';
import type { DecoratedProduct } from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';

import type { Dispatch, GetState, FullState } from '../../types';

export type TemporaryCart = {
  cartId: string,
  discounts: Array<string> | void,
  distinctProducts: number,
  cartProducts: Array<DecoratedProduct>,
  currency: Currency,
};

const isSessionStorageAvailable = (): boolean =>
  typeof window !== 'undefined' && !!window.sessionStorage;

export const selectOrderError: (
  state: FullState,
  props: {
    // XXX: need to refactor cf. OrderIdProp
    orderId: OrderId,
  },
) => boolean = (state, props) => {
  // $FlowFixMe[incompatible-call] - to be fixed in @gandi/billing, type OrderId is string here
  const order = selectOrder(state, props);
  return (
    // $FlowFixMe[incompatible-call] - to be fixed in @gandi/billing, type OrderId is string here
    !order || selectIsFailureOneOrder(state, props) || order.state !== 'init'
  );
};

type SelectShouldFetchOrderProps = {
  orderId: OrderIdProp,
};
const selectShouldFetchOrder = createSelector<
  FullState,
  SelectShouldFetchOrderProps,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
>(
  [
    selectIsLoadedOneOrder,
    selectIsFetchingOneOrder,
    selectIsFailureOneOrder,
    selectIsToRefetchOneOrder,
  ],
  (
    isLoaded: boolean,
    isFetching: boolean,
    isFailure: boolean,
    isToRefetch: boolean,
  ) => !isFetching && ((!isLoaded && !isFailure) || isToRefetch),
);

export const fetchOrderIfNeeded =
  (
    orderId: OrderIdProp,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const sharingIdProps = { sharingId: selectCurrentId(getState()) };
    if (selectShouldFetchOrder(getState(), { orderId: Number(orderId) })) {
      return dispatch(fetchOrder(orderId, sharingIdProps));
    }
    return Promise.resolve();
  };

export const fetchOrderThunk: (orderId: OrderIdProp) => Dispatch => boolean =
  orderId => dispatch =>
    dispatch(fetchOrderIfNeeded(orderId));

export const setTemporaryCartTracking = (
  temporaryCart: TemporaryCart,
): void => {
  if (isSessionStorageAvailable()) {
    window.sessionStorage.setItem(
      'commerceCart',
      JSON.stringify(temporaryCart),
    );
  }
};

export const getTemporaryCartTracking = (): TemporaryCart | void => {
  if (isSessionStorageAvailable()) {
    const temporaryCart = window.sessionStorage.getItem('commerceCart');
    return JSON.parse(temporaryCart);
  }
  return undefined;
};
